import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Smooth-scrolls to the top of the page, where the product gallery is. Deferred a tick: a tap on a variant label
 * focuses its (visually hidden) radio after the click handlers run. The radio is blurred too, otherwise the
 * variant picker morph restores its focus once the new variant has loaded and the browser scrolls back down to it.
 */
function scrollToGallery() {
  setTimeout(() => {
    const focused = document.activeElement;
    // Keyboard users keep their focus (it is :focus-visible); only a tap/click focus is dropped
    if (
      focused instanceof HTMLInputElement &&
      focused.closest('.variant-option__button-label') &&
      !focused.matches(':focus-visible')
    ) {
      focused.blur();
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 0);
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#handleSelectedVariantClick, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Re-clicking the variant that is already selected fires no product select event, so on page load the
   * preselected colour looked dead. Scroll back to the gallery for it too, like picking any other variant.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleSelectedVariantClick = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const label = event.target.closest('.variant-option__button-label');
    if (!label?.querySelector('input:checked')) return;

    scrollToGallery();
  };

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Head back to the gallery as soon as a variant is picked, instead of after the section has loaded
    scrollToGallery();

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        scrollToGallery();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
